import { useState, useEffect, useMemo, useCallback } from "react";
import Table, { TableHeading, TableControls, TableFooter } from "@/components/Table";
import EmptyState from "@/components/EmptyState";
import { FormProvider, useForm } from "react-hook-form";
import { useParams as useParamsHook, useSearchWithParams } from "@/hooks/useSearchParams";
import { StudentInternshipsQueryVariables, StudentInternship } from "../types";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import { STUDENT_INTERNSHIPS_QUERY } from "../queries";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import FacilitySideSheet from "@/features/facility/components/FacilitySideSheet";
import usePagination from "@/hooks/usePagination";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import { buildColumns } from "../_utils";
import MultiSelect from "@/components/MultiSelect";
import DateSelect from "@/components/DateSelect";
import FacilityAutocomplete from "../../../../../billing/_components/FacilityAutocomplete";
import SearchInput from "@/components/SearchInput";
import InternshipModal, { type InternshipFormData } from "@/components/InternshipModal";
import {
  CREATE_STUDENT_INTERNSHIP_MUTATION,
  UPDATE_STUDENT_INTERNSHIP_MUTATION,
  type CreateStudentInternshipInput,
  type UpdateStudentInternshipInput
} from "../mutations";
import { showSnackbar } from "@/lib/snackbarUtils";
import { useUniversityStaffPermissions } from "@/hooks/useUniversityStaffPermissions";
import InternshipHoursCalculatorDialog from "../_components/InternshipHoursCalculator";
import { MaterialNames } from "@/components/material_symbol_names";
import { ALL_US_STATES_ABBRV } from "@/lib/usStateHelpers";

const stateOptions = ALL_US_STATES_ABBRV.map((value) => ({ value, label: value }));

const extractDateString = (dateValue: any): string | null => {
  if (typeof dateValue === "object" && dateValue?.startDate && dateValue.startDate !== "") {
    return dateValue.startDate;
  }
  if (typeof dateValue === "string" && dateValue !== "") {
    return dateValue;
  }
  return null;
};

interface InternshipsTableProps {
  isAlumni: boolean;
  heading: string;
  body: string;
  searchPlaceholder: string;
  testId: string;
  showStatusFilter?: boolean;
  showActionButtons?: boolean;
}

export function InternshipsTable({
  isAlumni,
  heading,
  body,
  searchPlaceholder,
  testId,
  showStatusFilter = false,
  showActionButtons = false
}: InternshipsTableProps) {
  const { shouldShowFacilityProfileActions } = useUniversityStaffPermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const { universityId } = useParams();
  const { paginationVariables, createHeaderPagination, createFooterPagination, resetPagination } = usePagination({
    defaultPageSize: 20
  });
  const { isCompactLayout } = useIsCompactLayout();

  const [selectedFacility, setSelectedFacility] = useState<StudentInternship | null>(null);
  const [isDetailsPanelOpen, setIsDetailsPanelOpen] = useState(false);
  const [isAddInternshipModalOpen, setIsAddInternshipModalOpen] = useState(false);
  const [isEditInternshipModalOpen, setIsEditInternshipModalOpen] = useState(false);
  const [selectedInternshipForEdit, setSelectedInternshipForEdit] = useState<StudentInternship | null>(null);
  const [isCalculatorOpen, setIsCalculatorOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { params, addSearchParam, deleteSearchParams } = useParamsHook();
  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({
    paramName: "searchTerm"
  });

  const [selectedStatus, setSelectedStatus] = useState<string[]>(params.status ? params.status.split(",") : []);
  const [selectedStates, setSelectedStates] = useState<string[]>(params.states ? params.states.split(",") : []);
  const [selectedDateRange, setSelectedDateRange] = useState<{ from: string; to: string } | undefined>(
    params.startedOnFrom && params.startedOnTo ? { from: params.startedOnFrom, to: params.startedOnTo } : undefined
  );
  const [dateFieldValue, setDateFieldValue] = useState<{ startDate: Date | null; endDate: Date | null } | null>(
    params.startedOnFrom && params.startedOnTo
      ? { startDate: new Date(params.startedOnFrom), endDate: new Date(params.startedOnTo) }
      : null
  );

  const [sortingState, setSortingState] = useState([{ id: "startedOn", desc: true }]);

  const form = useForm();
  const { reset, setValue, watch, resetField } = form;
  const facilityIds = watch("facilityIds");

  const { Menu, setMenu } = useTableRowMenu();

  useEffect(() => {
    setSelectedStates(params.states ? params.states.split(",") : []);
  }, [params.states]);

  const variables = useMemo<StudentInternshipsQueryVariables>(
    () => ({
      input: {
        alumni: isAlumni,
        universityId: universityId?.toString() || "",
        search: debouncedSearch || undefined,
        internshipStatuses:
          showStatusFilter && params.status && params.status.length > 0 ? params.status.split(",") : undefined,
        facilityId: facilityIds?.length ? facilityIds[0] : params.facility || undefined,
        states: selectedStates.length > 0 ? selectedStates : undefined,
        startedOnFrom: params.startedOnFrom || undefined,
        startedOnTo: params.startedOnTo || undefined
      }
    }),
    [
      isAlumni,
      universityId,
      debouncedSearch,
      showStatusFilter,
      params.status,
      params.facility,
      params.startedOnFrom,
      params.startedOnTo,
      facilityIds,
      selectedStates
    ]
  );

  const { data, loading, refetch } = useQuery(STUDENT_INTERNSHIPS_QUERY, {
    variables: {
      ...variables,
      ...paginationVariables
    }
  });

  useEffect(() => {
    if (loading && params.facilityIds) {
      setValue("facilityIds", params.facilityIds);
    }
  }, [loading, params, setValue]);

  const internships = useMemo(() => {
    return data?.studentInternships?.edges?.map((edge) => edge.node) || [];
  }, [data]);

  const handleViewFacility = useCallback((internship: StudentInternship) => {
    setSelectedFacility(internship);
    setIsDetailsPanelOpen(true);
  }, []);

  const handleEditInternship = useCallback((internship: StudentInternship) => {
    setSelectedInternshipForEdit(internship);
    setIsEditInternshipModalOpen(true);
  }, []);

  const handleViewStudentInternships = useCallback(
    (internship: StudentInternship) => {
      const returnUrl = encodeURIComponent(`${location.pathname}${location.search}`);
      navigate(
        `/education/universities/${universityId}/students/${internship.student.pgaId}/internships?returnUrl=${returnUrl}`
      );
    },
    [location.pathname, location.search, navigate, universityId]
  );

  const handleCloseEditInternshipModal = useCallback(() => {
    setIsEditInternshipModalOpen(false);
    setSelectedInternshipForEdit(null);
    setError(null);
  }, []);

  const [updateStudentInternship] = useMutation(UPDATE_STUDENT_INTERNSHIP_MUTATION, {
    onCompleted: (data) => {
      const response = data.updateStudentInternship;
      if (response.success) {
        showSnackbar(response.message || "Internship updated");
        handleCloseEditInternshipModal();
        refetch();
      } else {
        setError(response.message || "Error updating internship");
      }
    },
    onError: (error) => {
      console.error("Error updating internship:", error);
      setError("Error updating internship");
    }
  });

  const handleSubmitEditInternship = useCallback(
    async (data: InternshipFormData) => {
      if (!selectedInternshipForEdit) return;

      try {
        const startDate = extractDateString(data.startDate) || data.startDate;
        const endDate = extractDateString(data.endDate);

        const graphqlInput: UpdateStudentInternshipInput = {
          startedOn: startDate as string,
          endedOn: endDate,
          workingLocation: "ON_SITE"
        };

        await updateStudentInternship({
          variables: {
            employmentId: selectedInternshipForEdit.id,
            facilityId: data.facilityId,
            supervisorId: data.supervisorId,
            input: graphqlInput
          }
        });
      } catch (error) {
        console.error("Error updating internship:", error);
      }
    },
    [updateStudentInternship, selectedInternshipForEdit]
  );

  const handleOpenAddInternshipModal = useCallback(() => {
    setIsAddInternshipModalOpen(true);
  }, []);

  const handleCloseAddInternshipModal = useCallback(() => {
    setIsAddInternshipModalOpen(false);
    setError(null);
  }, []);

  const [createStudentInternship] = useMutation(CREATE_STUDENT_INTERNSHIP_MUTATION, {
    onCompleted: (data) => {
      const response = data.createStudentInternship;
      if (response.success) {
        showSnackbar(response.message || "Internship created");
        handleCloseAddInternshipModal();
        refetch();
      } else {
        setError(response.message || "Error creating internship");
      }
    },
    onError: (error) => {
      console.error("Error creating internship:", error);
      setError("Error creating internship");
    }
  });

  const handleSubmitInternship = useCallback(
    async (data: InternshipFormData) => {
      try {
        const startDate = extractDateString(data.startDate) || data.startDate;
        const endDate = extractDateString(data.endDate);

        const graphqlInput: CreateStudentInternshipInput = {
          startedOn: startDate as string,
          endedOn: endDate,
          workingLocation: "ON_SITE"
        };

        await createStudentInternship({
          variables: {
            studentId: data.studentId || "",
            facilityId: data.facilityId,
            supervisorId: data.supervisorId,
            input: graphqlInput
          }
        });
      } catch (error) {
        console.error("Error creating internship:", error);
      }
    },
    [createStudentInternship]
  );

  const columns = useMemo(
    () =>
      buildColumns({
        isCompactLayout,
        handleViewFacility,
        handleEditInternship: showActionButtons ? handleEditInternship : undefined,
        handleViewStudentInternships: showActionButtons ? handleViewStudentInternships : undefined,
        setMenu: showActionButtons ? setMenu : undefined,
        actionId: showActionButtons ? actionId : undefined,
        isAlumni
      }),
    [
      isCompactLayout,
      handleViewFacility,
      handleEditInternship,
      handleViewStudentInternships,
      setMenu,
      showActionButtons,
      isAlumni
    ]
  );

  const filterState = {
    dates: !!selectedDateRange,
    facility: !!variables.input?.facilityId,
    states: selectedStates.length > 0,
    ...(showStatusFilter && { status: selectedStatus.length > 0 })
  };

  const activeFilterCount = Object.values(filterState).filter(Boolean).length;

  const handleClearAllFilters = () => {
    setSelectedStatus([]);
    setSelectedFacility(null);
    setSelectedStates([]);
    setSelectedDateRange(undefined);
    setDateFieldValue(null);
    resetField("facilityIds");
    resetField("facility");

    clearSearch();
    deleteSearchParams(["status", "facility", "facilityIds[]", "states", "startedOnFrom", "startedOnTo"]);
    reset({ dates: { startDate: null, endDate: null } as any });
    resetPagination();
  };

  const tableActions = showActionButtons
    ? [
        {
          label: "Add Internship",
          variant: "filled" as const,
          icon: "add" as MaterialNames,
          onClick: handleOpenAddInternshipModal,
          testId: "open-internship-dialog-button"
        },
        {
          label: "Internship Hours Calculator",
          variant: "outlined" as const,
          icon: "calculate" as MaterialNames,
          onClick: () => setIsCalculatorOpen(true),
          testId: "open-internship-calculator-button"
        }
      ]
    : undefined;

  return (
    <>
      <TableHeading heading={heading} body={body} actions={tableActions} />
      <Table
        data={internships}
        loading={loading}
        columns={columns}
        sortingState={sortingState}
        onSortingChange={setSortingState}
        onClickRow={(row) => {
          if (isAlumni) {
            handleViewFacility(row.original);
          } else {
            const returnUrl = encodeURIComponent(`${location.pathname}${location.search}`);
            navigate(
              `/education/universities/${universityId}/students/${row.original.student.pgaId}/internships?returnUrl=${returnUrl}`
            );
          }
        }}
        data-testid={testId}
        hasActiveFilters={searchValue || activeFilterCount > 0}
        renderNoDataEmptyState={() => (
          <EmptyState
            title={isAlumni ? "No Alumni Internships" : "No Internships"}
            caption="There are no records to display at this time"
            iconName="business_center"
          />
        )}
        renderControls={() => (
          <FormProvider {...form}>
            <TableControls
              pagination={createHeaderPagination(data?.studentInternships)}
              useExpandedFilters={true}
              expandedFiltersState={filterState}
              activeFilters={activeFilterCount}
              onClearAllFilters={handleClearAllFilters}
            >
              <SearchInput
                placeholder={searchPlaceholder}
                query={searchValue}
                onClear={() => {
                  clearSearch();
                  resetPagination();
                }}
                onChange={(value) => {
                  setSearchValue(value);
                  resetPagination();
                }}
              />
              {showStatusFilter && (
                <div data-name="status">
                  <MultiSelect
                    label="Status"
                    options={[
                      { value: "active", label: "In Progress" },
                      { value: "inactive", label: "Completed" }
                    ]}
                    selectedValues={selectedStatus}
                    onSelect={(value, checked) => {
                      const updatedStatus = checked
                        ? [...selectedStatus, value]
                        : selectedStatus.filter((v) => v !== value);

                      setSelectedStatus(updatedStatus);

                      if (updatedStatus.length > 0) {
                        addSearchParam("status", updatedStatus.join(","));
                      } else {
                        deleteSearchParams("status");
                      }
                      resetPagination();
                    }}
                    onClear={() => {
                      setSelectedStatus([]);
                      deleteSearchParams("status");
                      resetPagination();
                    }}
                  />
                </div>
              )}
              <div
                data-name="facility"
                className={isCompactLayout ? "w-full" : ""}
                style={isCompactLayout ? undefined : { minWidth: "300px", width: "300px" }}
              >
                <FacilityAutocomplete
                  params={params}
                  addSearchParam={addSearchParam}
                  deleteSearchParams={deleteSearchParams}
                  name="facility"
                />
              </div>
              <div data-name="states">
                <MultiSelect
                  label="States"
                  options={stateOptions}
                  selectedValues={selectedStates}
                  onSelect={(value, checked) => {
                    const updatedStates = checked
                      ? [...selectedStates, value]
                      : selectedStates.filter((state) => state !== value);

                    setSelectedStates(updatedStates);

                    if (updatedStates.length > 0) {
                      addSearchParam("states", updatedStates.join(","));
                    } else {
                      deleteSearchParams("states");
                    }
                    resetPagination();
                  }}
                  onClear={() => {
                    setSelectedStates([]);
                    deleteSearchParams("states");
                    resetPagination();
                  }}
                />
              </div>
              <div data-name="dates" data-filter-size="custom">
                <DateSelect
                  key={dateFieldValue ? `date-${dateFieldValue.startDate}-${dateFieldValue.endDate}` : "date-empty"}
                  label="Internship Date Range"
                  placeholder="Select Date Range"
                  useSingleDate={false}
                  useModal={true}
                  value={dateFieldValue}
                  onChange={(e) => {
                    if (typeof e === "object" && e !== null && e.startDate && e.endDate) {
                      const startDate =
                        e.startDate instanceof Date ? e.startDate.toISOString().split("T")[0] : e.startDate.toString();
                      const endDate =
                        e.endDate instanceof Date ? e.endDate.toISOString().split("T")[0] : e.endDate.toString();

                      setDateFieldValue({ startDate: e.startDate as Date, endDate: e.endDate as Date });
                      setSelectedDateRange({ from: startDate, to: endDate });

                      addSearchParam("startedOnFrom", startDate);
                      addSearchParam("startedOnTo", endDate);
                      resetPagination();
                    } else {
                      setDateFieldValue(null);
                      setSelectedDateRange(undefined);

                      deleteSearchParams(["startedOnFrom", "startedOnTo"]);
                      resetPagination();
                    }
                  }}
                />
              </div>
            </TableControls>
          </FormProvider>
        )}
        renderFooter={() => <TableFooter pagination={createFooterPagination(data?.studentInternships)} />}
      />

      <Menu />

      <FacilitySideSheet
        open={isDetailsPanelOpen && !!selectedFacility}
        onClose={() => setIsDetailsPanelOpen(false)}
        facilityId={selectedFacility?.facility?.id}
        customActionButtons={
          shouldShowFacilityProfileActions
            ? [
                {
                  text: "View Facility",
                  variant: "outlined" as const,
                  onClick: () => {
                    if (selectedFacility?.facility?.id) {
                      window.open(`/facilities/${selectedFacility.facility.id}/details/contact`, "_blank");
                    }
                  }
                }
              ]
            : []
        }
      />

      {showActionButtons && (
        <>
          <InternshipHoursCalculatorDialog open={isCalculatorOpen} onClose={() => setIsCalculatorOpen(false)} />

          <InternshipModal
            isOpen={isAddInternshipModalOpen}
            onClose={handleCloseAddInternshipModal}
            onSubmit={handleSubmitInternship}
            universityId={universityId}
            error={error}
            onClearError={() => setError(null)}
          />

          <InternshipModal
            isOpen={isEditInternshipModalOpen}
            onClose={handleCloseEditInternshipModal}
            selectedStudent={selectedInternshipForEdit?.student}
            selectedInternship={selectedInternshipForEdit}
            onSubmit={handleSubmitEditInternship}
            universityId={universityId}
            error={error}
            onClearError={() => setError(null)}
          />
        </>
      )}
    </>
  );
}
